import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/acp-mobile-sdkdocs/acp-mobile-sdkdocs/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
import Tabs from './tabs/index.md';
import Alerts from '../../resources/alerts.md';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Media = makeShortcode("Media");
const TabsBlock = makeShortcode("TabsBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "signal"
    }}>{`Signal`}</h1>
    <Alerts query="platform=outdated-version&componentClass=InlineNestedAlert" mdxType="Alerts" />
    <p>{`The Signal extension allows marketers to send a "signal" to their apps through the Adobe Experience Platform Mobile SDKs. This signal might tell the Mobile SDKs or the apps to complete tasks, such as send PII-labeled data, to trigger a postback to a third-party ad-network and open an app deep link or URL. To ensure that signals are sent or are activated, the marketers need to configure triggers and traits in the Data Collection UI.`}</p>
    <p>{`The Signal extension is bundled with the `}<a parentName="p" {...{
        "href": "../index.md"
      }}>{`MobileCore (Android)/ACPCore (iOS)`}</a>{` extension and allows you to send postbacks to third-party endpoints and open URLs, such as web URLs or application deep links, when using rules actions in the Data Collection UI.`}</p>
    <p>{`To send PII data to external destinations, the `}<inlineCode parentName="p">{`PII`}</inlineCode>{` action can trigger the Rules Engine when certain triggers and traits match. When setting a rule, you can also set the `}<inlineCode parentName="p">{`PII`}</inlineCode>{` action for a Signal event. The `}<inlineCode parentName="p">{`collectPii`}</inlineCode>{` API can then be used to trigger the rule and send the PII data to a remote server.`}</p>
    <p>{`To get started with Signal extension, complete the following steps:`}</p>
    <ol>
      <li parentName="ol">{`Add the `}<strong parentName="li">{`Signal`}</strong>{` extension to your app.`}</li>
      <li parentName="ol">{`Define the necessary rules in the Data Collection UI. `}</li>
      <li parentName="ol">{`(Optional) When using Send PII actions in the Data Collection UI, implement the APIs to collect PII data and send it to the configured third party destination.`}</li>
    </ol>
    <p>{`For more information about creating and configuring a rule in the Data Collection UI, see `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/tags/ui/rules.html"
      }}>{`Rules`}</a>{`.`}</p>
    <h2 {...{
      "id": "watch-the-video"
    }}>{`Watch the video`}</h2>
    <Media slots="video" mdxType="Media" />
    <p><a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=r-z9ivQjzOY"
      }}>{`https://www.youtube.com/watch?v=r-z9ivQjzOY`}</a></p>
    <h2 {...{
      "id": "add-the-signal-extension-to-your-app"
    }}>{`Add the Signal extension to your app`}</h2>
    <TabsBlock orientation="horizontal" slots="heading, content" repeat="6" mdxType="TabsBlock" />
    <p>{`iOS`}</p>
    <Tabs query="platform=ios&task=add" mdxType="Tabs" />
    <p>{`React Native`}</p>
    <Tabs query="platform=react-native&task=add" mdxType="Tabs" />
    <p>{`Flutter`}</p>
    <Tabs query="platform=flutter&task=add" mdxType="Tabs" />
    <p>{`Cordova`}</p>
    <Tabs query="platform=cordova&task=add" mdxType="Tabs" />
    <p>{`Unity`}</p>
    <Tabs query="platform=unity&task=add" mdxType="Tabs" />
    <p>{`Xamarin`}</p>
    <Tabs query="platform=xamarin&task=add" mdxType="Tabs" />
    <h2 {...{
      "id": "register-the-signal-extension"
    }}>{`Register the Signal extension`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`registerExtension()`}</inlineCode>{` API registers the Signal extension with the Mobile Core extension. This API allows the extension to send and receive events to and from the Mobile SDK.`}</p>
    <p>{`To register the Identity extension, use the following code sample:`}</p>
    <TabsBlock orientation="horizontal" slots="heading, content" repeat="6" mdxType="TabsBlock" />
    <p>{`iOS`}</p>
    <Tabs query="platform=ios&task=register" mdxType="Tabs" />
    <p>{`React Native`}</p>
    <Tabs query="platform=react-native&task=register" mdxType="Tabs" />
    <p>{`Flutter`}</p>
    <Tabs query="platform=flutter&task=register" mdxType="Tabs" />
    <p>{`Cordova`}</p>
    <Tabs query="platform=cordova&task=register" mdxType="Tabs" />
    <p>{`Unity`}</p>
    <Tabs query="platform=unity&task=register" mdxType="Tabs" />
    <p>{`Xamarin`}</p>
    <Tabs query="platform=xamarin&task=register" mdxType="Tabs" />
    <h2 {...{
      "id": "implement-the-mobile-sdk-to-send-pii-data-to-external-destinations"
    }}>{`Implement the Mobile SDK to send PII data to external destinations`}</h2>
    <p>{`To send PII data to external destinations, the `}<inlineCode parentName="p">{`PII`}</inlineCode>{` action can trigger the Rules Engine when the configured triggers and traits match. When creating a rule, you can set the `}<inlineCode parentName="p">{`PII`}</inlineCode>{` action for a Signal event, so that `}<inlineCode parentName="p">{`collectPii`}</inlineCode>{` can trigger the rule and send the `}<inlineCode parentName="p">{`PII`}</inlineCode>{` data.`}</p>
    <p>{`For more information about `}<inlineCode parentName="p">{`collectPii`}</inlineCode>{` and its usage, see `}<a parentName="p" {...{
        "href": "../api-reference.md#collect-pii"
      }}>{`collectPii`}</a>{`.`}</p>
    <p>{`For more information about how to configure the Signal postbacks in the Data Collection UI, see `}<a parentName="p" {...{
        "href": "../../user-guides/rules-engine-integration"
      }}>{`Signal extension and Rules Engine integration`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      